import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, asList, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import OrderStatusIndicator from "@/components/OrderStatusIndicator";
import { formatDate } from "@/lib/format";
import { Plus, Search, X, Printer, FileUp, Loader2, AlertCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Bepaal waar een order naartoe linkt afhankelijk van scan_status.
//   "scanning" → geen link, alleen visuele indicator
//   "scanned"  → direct naar bewerken zodat user kan controleren/aanvullen
//   "scan_failed" → naar bewerken (user kan order verwijderen of opnieuw scannen)
//   null (handmatig) → bestaande detail-pagina
function _orderOpenPath(o) {
    if (o.scan_status === "scanning") return null;
    if (o.scan_status === "scanned" || o.scan_status === "scan_failed") {
        return `/bestellingen/${o.id}/bewerken`;
    }
    return `/bestellingen/${o.id}`;
}

const BUCKET_ORDER = [
    { key: "scanning", label: "Bezig met scannen", tone: "text-florefy-900", cardBorder: "border-florefy-400", headerBg: "bg-florefy-50" },
    { key: "review", label: "Scan controleren", tone: "text-sky-800", cardBorder: "border-sky-300", headerBg: "bg-sky-50" },
    { key: "today", label: "Vandaag", tone: "text-florefy-900", cardBorder: "border-florefy-700", headerBg: "bg-florefy-50" },
    { key: "tomorrow", label: "Morgen", tone: "text-amber-800", cardBorder: "border-amber-300", headerBg: "bg-amber-50/60" },
    { key: "thisweek", label: "Deze week", tone: "text-ink-700", cardBorder: "border-border", headerBg: "bg-ink-50" },
    { key: "later", label: "Later", tone: "text-ink-500", cardBorder: "border-border", headerBg: "bg-ink-50/60" },
    { key: "past", label: "Verleden", tone: "text-ink-400", cardBorder: "border-border", headerBg: "bg-ink-50/40" },
];

/**
 * Bestellingen-overzicht. De raaplijst-bulk-functionaliteit is verhuisd naar
 * /printcentrum, daarom geen checkboxes meer in deze tabel. Per rij tonen we
 * 3 status-indicatoren: Berekend / Bewerkt / Labels geprint.
 */

const STATUS_OPTIONS = [
    { value: "", label: "Alle" },
    { value: "open", label: "Open (niet berekend)" },
    { value: "calculated", label: "Berekend" },
    { value: "stale", label: "Bewerkt na berekening" },
    { value: "printed", label: "Labels geprint" },
    { value: "not_printed", label: "Labels nog niet geprint" },
];

const DATE_OPTIONS = [
    { value: "today", label: "Vandaag" },
    { value: "tomorrow", label: "Morgen" },
    { value: "week", label: "Deze week" },
    { value: "next30", label: "Komende 30 dagen" },
    { value: "past", label: "Verleden" },
    { value: "all", label: "Alles" },
];

// Bepaal of een ISO-date binnen het gekozen bereik valt (vergeleken met vandaag).
function _inDateRange(deliveryDate, range, today) {
    if (!deliveryDate || range === "all") return true;
    const d = new Date(deliveryDate + "T00:00:00");
    const t = new Date(today + "T00:00:00");
    const diffDays = Math.round((d - t) / 86400000);
    switch (range) {
        case "today":
            return diffDays === 0;
        case "tomorrow":
            return diffDays === 1;
        case "week": {
            // Maandag van deze week → zondag
            const dow = t.getDay() || 7; // 1=ma..7=zo
            const monday = new Date(t);
            monday.setDate(t.getDate() - (dow - 1));
            const sunday = new Date(monday);
            sunday.setDate(monday.getDate() + 6);
            return d >= monday && d <= sunday;
        }
        case "next30":
            return diffDays >= 0 && diffDays <= 30;
        case "past":
            return diffDays < 0;
        default:
            return true;
    }
}

// Groepeer orders in: scanning (bovenaan) / vandaag / morgen / deze week / later / verleden
function _bucketize(orders, today) {
    const buckets = {
        scanning: [],
        review: [],
        today: [],
        tomorrow: [],
        thisweek: [],
        later: [],
        past: [],
    };
    const t = new Date(today + "T00:00:00");
    const dow = t.getDay() || 7;
    const monday = new Date(t);
    monday.setDate(t.getDate() - (dow - 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    for (const o of orders) {
        // Scanning/failed orders altijd bovenaan, los van delivery_date.
        if (o.scan_status === "scanning" || o.scan_status === "scan_failed") {
            buckets.scanning.push(o);
            continue;
        }
        if (o.scan_status === "scanned") {
            buckets.review.push(o);
            continue;
        }
        if (!o.delivery_date) {
            buckets.later.push(o);
            continue;
        }
        const d = new Date(o.delivery_date + "T00:00:00");
        const diff = Math.round((d - t) / 86400000);
        if (diff < 0) buckets.past.push(o);
        else if (diff === 0) buckets.today.push(o);
        else if (diff === 1) buckets.tomorrow.push(o);
        else if (d <= sunday) buckets.thisweek.push(o);
        else buckets.later.push(o);
    }
    return buckets;
}

export default function Orders() {
    const [searchParams] = useSearchParams();
    const [orders, setOrders] = useState([]);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState(() => {
        const requested = searchParams.get("status") || "";
        return STATUS_OPTIONS.some((option) => option.value === requested) ? requested : "";
    });
    const [dateFilter, setDateFilter] = useState("next30");  // operationeel default
    const today = useMemo(() => {
        const now = new Date();
        now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
        return now.toISOString().slice(0, 10);
    }, []);
    const [scanUploading, setScanUploading] = useState(false);
    const [scanError, setScanError] = useState("");
    const [scanEnabled, setScanEnabled] = useState(false);
    const [scanUnavailableReason, setScanUnavailableReason] = useState("");
    const [dragOver, setDragOver] = useState(false);
    const [loadError, setLoadError] = useState("");
    const fileRef = useRef(null);
    const dragCounter = useRef(0);
    const scanUploadInFlight = useRef(false);
    const previousScanStatuses = useRef(new Map());

    const loadOrders = useCallback(() => {
        setLoadError("");
        return api.get("/orders")
            .then((r) => {
                const nextOrders = asList(r.data, ["orders"]);
                const previous = previousScanStatuses.current;
                const completed = nextOrders.filter(
                    (order) => previous.get(order.id) === "scanning" && order.scan_status === "scanned"
                );
                const failed = nextOrders.filter(
                    (order) => previous.get(order.id) === "scanning" && order.scan_status === "scan_failed"
                );
                previousScanStatuses.current = new Map(
                    nextOrders.map((order) => [order.id, order.scan_status || null])
                );
                setOrders(nextOrders);
                if (completed.length > 0) {
                    toast.success(
                        completed.length === 1
                            ? "Scan is klaar en staat bij 'Scan controleren'."
                            : `${completed.length} scans zijn klaar om te controleren.`
                    );
                }
                if (failed.length > 0) {
                    toast.error(
                        failed.length === 1
                            ? "Een scan kon niet worden verwerkt."
                            : `${failed.length} scans konden niet worden verwerkt.`
                    );
                }
            })
            .catch((e) => setLoadError(formatApiErrorDetail(e.response?.data?.detail ?? "Bestellingen konden niet worden geladen")));
    }, []);

    useEffect(() => {
        loadOrders();
        api.get("/config")
            .then((r) => {
                setScanEnabled(!!r.data.scan_enabled);
                setScanUnavailableReason(r.data.scan_unavailable_reason || "");
            })
            .catch(() => {
                setScanEnabled(false);
                setScanUnavailableReason("De scannerconfiguratie kon niet worden geladen.");
            });
    }, [loadOrders]);

    // Polling: als er nog orders zijn met scan_status="scanning", refresh elke 4s.
    const hasScanning = useMemo(
        () => orders.some((o) => o.scan_status === "scanning"),
        [orders]
    );
    useEffect(() => {
        if (!hasScanning) return;
        const id = setInterval(loadOrders, 4000);
        return () => clearInterval(id);
    }, [hasScanning, loadOrders]);

    const onScanFiles = useCallback(async (filesList) => {
        const files = Array.from(filesList || []);
        if (!files.length) return;
        if (scanUploadInFlight.current) {
            setScanError("Er wordt al een upload verwerkt. Wacht tot die is toegevoegd.");
            return;
        }
        scanUploadInFlight.current = true;
        setScanError("");
        setScanUploading(true);
        try {
            const form = new FormData();
            files.forEach((f) => form.append("files", f));
            const { data } = await api.post("/orders/scan-draft", form, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60000,
            });
            const createdCount = Number(
                data?.created_count ??
                data?.count ??
                (Array.isArray(data?.order_ids) ? data.order_ids.length : data?.order_id ? 1 : 0)
            );
            toast.success(
                createdCount === 1
                    ? "1 bestelling is toegevoegd aan de scanwachtrij."
                    : `${createdCount || files.length} bestellingen zijn toegevoegd aan de scanwachtrij.`
            );
            setSearch("");
            setStatusFilter("");
            setDateFilter("all");
            await loadOrders();  // pakt de scanning-order direct mee
        } catch (e) {
            setScanError(formatApiErrorDetail(e.response?.data?.detail ?? "Upload mislukt"));
        } finally {
            scanUploadInFlight.current = false;
            setScanUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    }, [loadOrders]);

    const deleteOrder = async (oid) => {
        if (!window.confirm("Bestelling verwijderen?")) return;
        try {
            await api.delete(`/orders/${oid}`);
            await loadOrders();
        } catch (e) {
            setScanError(formatApiErrorDetail(e.response?.data?.detail ?? "Bestelling verwijderen mislukt"));
        }
    };

    // Drag-and-drop handlers — window-niveau zodat user overal op de
    // bestellingen-pagina kan slepen. `dragCounter` voorkomt flicker bij
    // het over child-elementen slepen (elke enter/leave wordt geteld).
    useEffect(() => {
        if (!scanEnabled) return;
        const onDragEnter = (e) => {
            if (!e.dataTransfer?.types?.includes("Files")) return;
            e.preventDefault();
            if (scanUploadInFlight.current) return;
            dragCounter.current += 1;
            setDragOver(true);
        };
        const onDragOver = (e) => {
            if (!e.dataTransfer?.types?.includes("Files")) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
        };
        const onDragLeave = (e) => {
            if (!e.dataTransfer?.types?.includes("Files")) return;
            e.preventDefault();
            dragCounter.current = Math.max(0, dragCounter.current - 1);
            if (dragCounter.current === 0) setDragOver(false);
        };
        const onDrop = (e) => {
            if (!e.dataTransfer?.types?.includes("Files")) return;
            e.preventDefault();
            dragCounter.current = 0;
            setDragOver(false);
            if (scanUploadInFlight.current) {
                setScanError("Er wordt al een upload verwerkt. Wacht tot die is toegevoegd.");
                return;
            }
            const files = Array.from(e.dataTransfer.files || []);
            const accepted = files.filter((f) =>
                /\.(pdf|png|jpe?g|webp|heic|heif)$/i.test(f.name) ||
                /^(application\/pdf|image\/)/i.test(f.type)
            );
            if (accepted.length) onScanFiles(accepted);
            else if (files.length) setScanError("Alleen PDF of afbeeldingen kunnen worden geüpload.");
        };
        window.addEventListener("dragenter", onDragEnter);
        window.addEventListener("dragover", onDragOver);
        window.addEventListener("dragleave", onDragLeave);
        window.addEventListener("drop", onDrop);
        return () => {
            window.removeEventListener("dragenter", onDragEnter);
            window.removeEventListener("dragover", onDragOver);
            window.removeEventListener("dragleave", onDragLeave);
            window.removeEventListener("drop", onDrop);
        };
    }, [onScanFiles, scanEnabled]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const arr = orders.filter((o) => {
            // Scanning + failed orders ALTIJD zichtbaar, ongeacht filters,
            // zodat de user weet dat zijn upload nog bezig is.
            const isScanState = ["scanning", "scanned", "scan_failed"].includes(o.scan_status);
            if (isScanState) return true;
            // Datum-filter (skip voor scan-state)
            if (!_inDateRange(o.delivery_date, dateFilter, today)) return false;
            // Status filter (skip voor scan-state)
            if (statusFilter) {
                const calculated = !!o.last_calculation_id;
                const stale = !!o.is_stale;
                const printed = o.labels_printed_status === "geprint";
                if (statusFilter === "open" && calculated) return false;
                if (statusFilter === "calculated" && !calculated) return false;
                if (statusFilter === "stale" && !stale) return false;
                if (statusFilter === "printed" && !printed) return false;
                if (statusFilter === "not_printed" && printed) return false;
            }
            if (!q) return true;
            const hay = [
                o.customer?.name || "",
                o.id || "",
                o.delivery_date || "",
                o.notes || "",
                o.scan_filename || "",
                ...(o.lines || []).map((l) => l.product?.sku || ""),
                ...(o.lines || []).map((l) => l.product?.name || ""),
            ]
                .join(" ")
                .toLowerCase();
            return hay.includes(q);
        });
        // Sorteer oplopend op leverdatum (vroegste eerst). Orders zonder datum onderaan.
        arr.sort((a, b) => {
            const da = a.delivery_date || "9999-99-99";
            const db = b.delivery_date || "9999-99-99";
            return da.localeCompare(db);
        });
        return arr;
    }, [orders, search, statusFilter, dateFilter, today]);

    const buckets = useMemo(() => _bucketize(filtered, today), [filtered, today]);
    const bucketCounts = useMemo(() => ({
        today: orders.filter((o) => _inDateRange(o.delivery_date, "today", today)).length,
        tomorrow: orders.filter((o) => _inDateRange(o.delivery_date, "tomorrow", today)).length,
        week: orders.filter((o) => _inDateRange(o.delivery_date, "week", today)).length,
        next30: orders.filter((o) => _inDateRange(o.delivery_date, "next30", today)).length,
        past: orders.filter((o) => _inDateRange(o.delivery_date, "past", today)).length,
        all: orders.length,
    }), [orders, today]);

    return (
        <div className="p-5 sm:p-7 lg:p-10 xl:p-12 max-w-[1480px] mx-auto pb-24 lg:pb-12">
            {dragOver && (
                <div
                    className="fixed inset-0 z-50 bg-florefy-900/30 backdrop-blur-sm flex items-center justify-center pointer-events-none"
                    data-testid="drag-overlay"
                >
                    <div className="bg-white border-2 border-dashed border-florefy-700 rounded-xl px-12 py-10 shadow-2xl text-center max-w-lg">
                        <FileUp className="h-12 w-12 mx-auto text-florefy-700" />
                        <div className="mt-4 text-xl font-semibold text-florefy-900">
                            Sleep bestanden hier los
                        </div>
                        <div className="mt-1 text-sm text-ink-600">
                            PDF, JPG, PNG of HEIC — meerdere tegelijk werken
                        </div>
                    </div>
                </div>
            )}
            <PageHeader
                kicker="Werkstroom"
                title="Bestellingen"
                subtitle="Overzicht van alle bestellingen met hun status: berekend, bewerkt na berekening en of de labels zijn geprint."
                testid="orders-header"
                actions={
                    <>
                        <Link
                            to="/printcentrum"
                            data-testid="btn-go-print-center"
                            className="px-3 lg:px-4 py-2 rounded-md border border-border bg-white hover:bg-ink-50 text-sm flex items-center gap-2"
                        >
                            <Printer className="h-4 w-4" /> <span className="hidden sm:inline">Printcentrum</span>
                        </Link>
                        <input
                            ref={fileRef}
                            type="file"
                            accept="application/pdf,image/png,image/jpeg,image/webp,image/heic,image/heif"
                            multiple
                            className="hidden"
                            onChange={(e) => onScanFiles(e.target.files)}
                            data-testid="input-scan-upload"
                        />
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            disabled={scanUploading || !scanEnabled}
                            title={scanEnabled ? "Raaplijst automatisch inlezen" : scanUnavailableReason || "AI-scannen is niet actief"}
                            data-testid="btn-scan-upload"
                            className="px-3 lg:px-4 py-2 rounded-md border border-florefy-900 bg-white hover:bg-florefy-50 text-florefy-900 text-sm flex items-center gap-2 disabled:opacity-60"
                        >
                            {scanUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                            <span className="hidden sm:inline">
                                {scanUploading ? "Uploaden…" : scanEnabled ? "Scan uploaden" : "AI-scan niet actief"}
                            </span>
                        </button>
                        <Link
                            to="/bestellingen/nieuw"
                            data-testid="btn-new-order"
                            className="px-3 lg:px-4 py-2 rounded-md bg-florefy-900 hover:bg-florefy-700 text-white text-sm flex items-center gap-2"
                        >
                            <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Nieuwe bestelling</span>
                        </Link>
                    </>
                }
            />

            {loadError && (
                <div className="bg-red-50 border border-red-200 text-red-800 text-sm p-3 mb-3 rounded-md flex items-center justify-between gap-3" role="alert">
                    <span>{loadError}</span>
                    <button type="button" onClick={loadOrders} className="font-semibold underline">Opnieuw proberen</button>
                </div>
            )}

            {scanError && (
                <div className="bg-rose-50 border border-rose-300 text-rose-800 text-sm p-3 mb-3 rounded-md flex gap-2" data-testid="scan-upload-error">
                    <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" /> {scanError}
                </div>
            )}

            {/* Datum quick-filter chips */}
            <div className="flex items-center gap-1.5 flex-wrap mb-3" data-testid="orders-date-chips">
                {DATE_OPTIONS.map((opt) => {
                    const cnt = bucketCounts[opt.value] ?? 0;
                    const on = dateFilter === opt.value;
                    return (
                        <button
                            key={opt.value}
                            onClick={() => setDateFilter(opt.value)}
                            data-testid={`date-chip-${opt.value}`}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                                on
                                    ? "bg-florefy-900 border-florefy-900 text-white"
                                    : "bg-white border-border text-ink-700 hover:bg-ink-50"
                            }`}
                        >
                            {opt.label}
                            <span className={`font-mono text-[10px] ${on ? "text-white/70" : "text-ink-400"}`}>·{cnt}</span>
                        </button>
                    );
                })}
            </div>

            <div className="bg-white border border-border rounded-md mb-4 p-3 flex flex-wrap items-center gap-3" data-testid="orders-filters">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Zoek op klant, SKU, datum, notitie…"
                        data-testid="orders-search"
                        className="w-full pl-9 pr-9 py-2 text-sm border border-border rounded-md focus:outline-none focus:border-florefy-500"
                    />
                    {search && (
                        <button
                            onClick={() => setSearch("")}
                            data-testid="orders-search-clear"
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-ink-400 hover:text-ink-700"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    data-testid="orders-status-filter"
                    className="text-sm border border-border rounded-md px-3 py-2 bg-white focus:outline-none focus:border-florefy-500"
                >
                    {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
                <div
                    className="font-mono text-xs text-ink-500"
                    data-testid="orders-filter-count"
                >
                    {filtered.length} van {orders.length}
                </div>
            </div>

            {/* Mobiel: card-view gegroepeerd op datum-bucket */}
            <div className="lg:hidden space-y-4" data-testid="orders-mobile">
                {filtered.length === 0 && (
                    <div className="bg-white border border-border rounded-md px-4 py-8 text-center text-ink-500 text-sm">
                        {orders.length === 0
                            ? "Nog geen bestellingen."
                            : "Geen resultaten in dit filter."}
                    </div>
                )}
                {BUCKET_ORDER.filter((b) => buckets[b.key].length > 0).map((b) => (
                    <div key={b.key} data-testid={`bucket-mobile-${b.key}`}>
                        <h3 className={`font-mono text-[10px] uppercase tracking-widest mb-2 px-1 ${b.tone}`}>
                            {b.label} <span className="text-ink-400">({buckets[b.key].length})</span>
                        </h3>
                        <div className="space-y-2">
                            {buckets[b.key].map((o) => {
                                const path = _orderOpenPath(o);
                                const scanning = o.scan_status === "scanning";
                                const failed = o.scan_status === "scan_failed";
                                const needsReview = o.scan_status === "scanned";
                                const inner = (
                                    <>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0 flex-1">
                                                <div className="font-medium text-ink-900 truncate text-sm flex items-center gap-2">
                                                    {scanning && <Loader2 className="h-3.5 w-3.5 animate-spin text-florefy-700 shrink-0" />}
                                                    {failed && <AlertCircle className="h-3.5 w-3.5 text-rose-600 shrink-0" />}
                                                    {needsReview && <AlertCircle className="h-3.5 w-3.5 text-sky-700 shrink-0" />}
                                                    {scanning
                                                        ? <span className="italic text-ink-600">Scan wordt verwerkt…</span>
                                                        : (o.customer?.name || (failed ? "Scan mislukt" : "—"))}
                                                </div>
                                                <div className="text-xs text-ink-500 font-mono mt-0.5 truncate">
                                                    {scanning ? (o.scan_filename || "uploaden") : (
                                                        <>
                                                            {formatDate(o.delivery_date)} · {o.lines.length} regel{o.lines.length === 1 ? "" : "s"}
                                                            {failed && <span className="ml-1 text-rose-600">— {o.scan_error || "onbekende fout"}</span>}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            {!scanning && <div className="text-ink-300 shrink-0">→</div>}
                                            {failed && (
                                                <button
                                                    onClick={(e) => { e.preventDefault(); deleteOrder(o.id); }}
                                                    className="text-rose-600 hover:text-rose-800 p-1 shrink-0"
                                                    data-testid={`btn-delete-order-${o.id}`}
                                                    title="Bestelling verwijderen"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                        {needsReview ? (
                                            <div className="mt-2 text-xs font-semibold text-sky-800" data-testid={`scan-review-status-mobile-${o.id}`}>
                                                Controle vereist voordat deze bestelling kan worden verwerkt
                                            </div>
                                        ) : !scanning && !failed && (
                                            <div className="mt-2">
                                                <OrderStatusIndicator
                                                    calculated={!!o.last_calculation_id}
                                                    stale={!!o.is_stale}
                                                    updated_at={o.updated_at}
                                                    printed_status={o.labels_printed_status}
                                                    printed_count={o.labels_printed_count}
                                                    total_count={o.labels_total_count}
                                                    testid={`order-status-mobile-${o.id}`}
                                                />
                                            </div>
                                        )}
                                    </>
                                );
                                const baseCls = `block bg-white border ${b.cardBorder} rounded-md p-3 transition-colors`;
                                if (!path) {
                                    return (
                                        <div
                                            key={o.id}
                                            data-testid={`order-card-${o.id}`}
                                            className={`${baseCls} opacity-80 cursor-wait`}
                                        >{inner}</div>
                                    );
                                }
                                return (
                                    <Link
                                        key={o.id}
                                        to={path}
                                        data-testid={`order-card-${o.id}`}
                                        className={`${baseCls} active:bg-ink-50 ${failed ? "border-rose-300 bg-rose-50/40" : ""}`}
                                    >{inner}</Link>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {/* Desktop: tabel met sticky bucket-headers */}
            <div className="hidden lg:block bg-white border border-border rounded-md overflow-hidden">
                <table className="w-full text-sm" data-testid="orders-table">
                    <thead>
                        <tr className="bg-ink-50">
                            <th className="text-left px-4 py-3 data-label">Klant</th>
                            <th className="text-left px-4 py-3 data-label">Leverdatum</th>
                            <th className="text-left px-4 py-3 data-label">Regels</th>
                            <th className="text-left px-4 py-3 data-label">Aangemaakt</th>
                            <th className="text-left px-4 py-3 data-label">Status</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-5 py-12 text-center text-ink-500">
                                    {orders.length === 0
                                        ? "Nog geen bestellingen."
                                        : "Geen resultaten in dit filter."}
                                </td>
                            </tr>
                        )}
                        {BUCKET_ORDER.filter((b) => buckets[b.key].length > 0).map((b) => (
                            <React.Fragment key={b.key}>
                                <tr className={b.headerBg} data-testid={`bucket-desktop-${b.key}`}>
                                    <td colSpan={6} className={`px-4 py-2 font-mono text-[10px] uppercase tracking-widest ${b.tone}`}>
                                        {b.label}
                                        <span className="text-ink-400 ml-2">({buckets[b.key].length})</span>
                                    </td>
                                </tr>
                                {buckets[b.key].map((o) => {
                                    const path = _orderOpenPath(o);
                                    const scanning = o.scan_status === "scanning";
                                    const failed = o.scan_status === "scan_failed";
                                    const needsReview = o.scan_status === "scanned";
                                    return (
                                    <tr
                                        key={o.id}
                                        className={`border-t border-border/60 ${scanning ? "bg-florefy-50/50" : failed ? "bg-rose-50/50" : "hover:bg-ink-50"}`}
                                        data-testid={`order-row-${o.id}`}
                                    >
                                        <td className="px-4 py-3 font-medium text-ink-900">
                                            <span className="inline-flex items-center gap-2">
                                                {scanning && <Loader2 className="h-3.5 w-3.5 animate-spin text-florefy-700" data-testid={`scan-spinner-${o.id}`} />}
                                                {failed && <AlertCircle className="h-3.5 w-3.5 text-rose-600" />}
                                                {scanning
                                                    ? <span className="italic text-ink-600 font-normal">Scan wordt verwerkt…</span>
                                                    : (o.customer?.name || (failed ? "Scan mislukt" : "—"))}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 font-mono" title={o.delivery_date}>
                                            {scanning ? <span className="text-ink-400">—</span> : formatDate(o.delivery_date)}
                                        </td>
                                        <td className="px-4 py-3 font-mono">{scanning ? <span className="text-ink-400">—</span> : o.lines.length}</td>
                                        <td
                                            className="px-4 py-3 font-mono text-xs text-ink-500"
                                            title={new Date(o.created_at).toLocaleString("nl-NL")}
                                        >
                                            {formatDate(o.created_at)}
                                        </td>
                                        <td className="px-4 py-3">
                                            {scanning ? (
                                                <span className="text-xs text-florefy-800 font-medium" data-testid={`scan-status-${o.id}`}>
                                                    {o.scan_filename ? o.scan_filename : "Bezig met scannen…"}
                                                </span>
                                            ) : failed ? (
                                                <span className="text-xs text-rose-700" title={o.scan_error || ""}>
                                                    {o.scan_error || "Scan mislukt"}
                                                </span>
                                            ) : needsReview ? (
                                                <span className="text-xs font-semibold text-sky-800" data-testid={`scan-review-status-${o.id}`}>
                                                    Controle vereist
                                                </span>
                                            ) : (
                                                <OrderStatusIndicator
                                                    calculated={!!o.last_calculation_id}
                                                    stale={!!o.is_stale}
                                                    updated_at={o.updated_at}
                                                    printed_status={o.labels_printed_status}
                                                    printed_count={o.labels_printed_count}
                                                    total_count={o.labels_total_count}
                                                    testid={`order-status-${o.id}`}
                                                />
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            {failed && (
                                                <button
                                                    onClick={() => deleteOrder(o.id)}
                                                    className="text-rose-600 hover:text-rose-800 mr-3"
                                                    data-testid={`btn-delete-order-${o.id}`}
                                                    title="Verwijder"
                                                ><Trash2 className="h-4 w-4 inline" /></button>
                                            )}
                                            {path ? (
                                                <Link
                                                    to={path}
                                                    className="text-florefy-900 hover:text-florefy-700"
                                                    data-testid={`btn-open-order-${o.id}`}
                                                >
                                                    {o.scan_status === "scanned" ? "Controleer →" : "Open →"}
                                                </Link>
                                            ) : (
                                                <span className="text-ink-400 text-xs italic">— wachten —</span>
                                            )}
                                        </td>
                                    </tr>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
